import { Container, FillGradient, Graphics, Sprite, Text } from 'pixi.js';
import type {
  BackgroundCue,
  BackgroundDefinition,
  BackgroundEffect,
  BackgroundTransition
} from './types';
import backgroundsData from '../data/backgrounds.json';
import type { AssetLoader } from '../systems/AssetLoader';
import { UI_COLORS, UI_FONT_FAMILY } from '../ui';

export interface BackgroundManagerOptions {
  assetLoader: AssetLoader;
  width?: number;
  height?: number;
  backgrounds?: readonly BackgroundDefinition[];
}

export interface BackgroundDebugState {
  currentBackgroundId: string | null;
  label: string | null;
  category: BackgroundDefinition['category'] | null;
  known: boolean;
  currentEffect: BackgroundEffect;
  currentEffectIntensity: number;
  transitioning: boolean;
  placeholder: boolean;
  backgroundLayerChildCount: number;
  currentLayerChildCount: number;
  transitionPreviousChildCount: number;
  effectLayerChildCount: number;
  effectLayerAlpha: number;
  effectLayerVisible: boolean;
  effectLayerX: number;
  effectLayerY: number;
  backgroundLayerScaleX: number;
  backgroundLayerScaleY: number;
  backgroundLayerX: number;
  backgroundLayerY: number;
  containerX: number;
  containerY: number;
  rainLayerCount: number;
  noiseFrameCount: number;
  glitchLayerCount: number;
  shakeActive: boolean;
  shakeTimerMs: number;
  effectTimeMs: number;
}

interface BackgroundLayer {
  container: Container;
  placeholder: boolean;
}

interface TransitionState {
  elapsedMs: number;
  durationMs: number;
  transition: BackgroundTransition;
  previous: Container | null;
}

interface CameraTransform {
  scale: number;
  x: number;
  y: number;
}

interface RainLayerConfig {
  count: number;
  length: number;
  slant: number;
  strokeWidth: number;
  alpha: number;
  color: number;
  seedX: number;
  seedY: number;
  cycleY: number;
  highlightEvery: number;
  motionBlur: boolean;
}

interface NoiseFrameConfig {
  count: number;
  minSize: number;
  maxSize: number;
  alpha: number;
  lightColor: number;
  darkColor: number;
  seedOffset: number;
  scanlineAlpha: number;
  bandAlpha: number;
}

interface GlitchLayerSet {
  scanlines: Graphics;
  colorShift: Graphics;
  slices: Graphics;
  noiseFrames: Graphics[];
}

const DEFAULT_WIDTH = 1280;
const DEFAULT_HEIGHT = 720;
const DEFAULT_BACKGROUND_ID = 'black_screen';
const DEFAULT_EFFECT_INTENSITY = 1;
const ZOOM_IN_DURATION_MS = 14000;
const ZOOM_OUT_DURATION_MS = 12000;
const SLOW_PAN_DURATION_MS = 24000;
const SUBTLE_DRIFT_X_DURATION_MS = 7600;
const SUBTLE_DRIFT_Y_DURATION_MS = 9200;
const RAIN_BACK_CYCLE_Y = 84;
const RAIN_MID_CYCLE_Y = 98;
const RAIN_FRONT_CYCLE_Y = 112;
const RAIN_MIST_CYCLE_Y = 36;
const NOISE_FRAME_COUNT = 2;
const STATIC_NOISE_FRAME_COUNT = 3;
const GLITCH_NOISE_FRAME_COUNT = 2;
const GLITCH_BURST_DURATION_MS = 260;
const GLITCH_SECONDARY_BURST_START_MS = 860;
const GLITCH_SECONDARY_BURST_END_MS = 1020;
const GLITCH_LOOP_MS = 1680;
const BACKGROUNDS = (backgroundsData as { backgrounds: BackgroundDefinition[] }).backgrounds;
const DISPLAY_DESTROY_OPTIONS = { children: true, context: true } as const;
const CASE0_PLACEHOLDER_IDS = new Set([
  'clock_tower_outside',
  'detective_board',
  'detective_office_night',
  'jewelry_shop_exterior',
  'jewelry_shop_interior',
  'locked_room',
  'police_station',
  'town_night',
  'unresolved_stamp_bg'
]);

type PlaceholderMotif =
  | 'caseFile'
  | 'clockTower'
  | 'detectiveBoard'
  | 'detectiveOffice'
  | 'jewelryExterior'
  | 'jewelryInterior'
  | 'lockedRoom'
  | 'policeStation'
  | 'town';

export class BackgroundManager {
  public readonly container = new Container();

  private readonly backgroundLayer = new Container();
  private readonly transitionOverlay = new Graphics();
  private readonly effectLayer = new Container();
  private readonly backgroundById: Map<string, BackgroundDefinition>;
  private readonly assetLoader: AssetLoader;
  private width: number;
  private height: number;
  private currentLayer: BackgroundLayer | null = null;
  private currentBackgroundId: string | null = null;
  private currentEffect: BackgroundEffect = 'none';
  private currentEffectIntensity = DEFAULT_EFFECT_INTENSITY;
  private transitionState: TransitionState | null = null;
  private rainMistLayer: Graphics | null = null;
  private rainBackLayer: Graphics | null = null;
  private rainMidLayer: Graphics | null = null;
  private rainFrontLayer: Graphics | null = null;
  private rainSplashLayer: Graphics | null = null;
  private noiseFrames: Graphics[] = [];
  private glitchLayers: GlitchLayerSet | null = null;
  private requestToken = 0;
  private effectTimeMs = 0;
  private shakeTimerMs = 0;
  private shakeIntensity = 0;
  private destroyed = false;

  public constructor(options: BackgroundManagerOptions) {
    this.assetLoader = options.assetLoader;
    this.width = options.width ?? DEFAULT_WIDTH;
    this.height = options.height ?? DEFAULT_HEIGHT;
    this.backgroundById = new Map(
      (options.backgrounds ?? BACKGROUNDS).map((background) => [background.id, background])
    );
    this.container.addChild(this.backgroundLayer);
    this.container.addChild(this.transitionOverlay);
    this.container.addChild(this.effectLayer);
  }

  public async setBackground(cue: BackgroundCue | undefined): Promise<void> {
    if (this.destroyed) {
      return;
    }

    const token = ++this.requestToken;
    const resolvedCue = this.resolveCue(cue);
    const nextLayer = await this.createLayer(resolvedCue);
    if (this.destroyed || token !== this.requestToken) {
      nextLayer.container.destroy(DISPLAY_DESTROY_OPTIONS);
      return;
    }

    this.currentBackgroundId = resolvedCue.id;
    this.currentEffect =
      resolvedCue.effect ?? this.getDefinition(resolvedCue.id)?.defaultEffect ?? 'none';
    this.currentEffectIntensity = resolvedCue.effectIntensity;
    this.effectTimeMs = 0;
    this.clearShake();
    if (this.currentEffect === 'shake') {
      this.shake(0.35, 360);
    }
    this.drawEffectLayer();
    this.updateCameraAnimation();
    this.applyLayerTransition(
      nextLayer,
      resolvedCue.transition ?? 'cut',
      resolvedCue.duration ?? 0
    );
  }

  public resize(width: number, height: number): void {
    const nextWidth = Math.max(1, Math.round(width));
    const nextHeight = Math.max(1, Math.round(height));
    const sizeChanged = nextWidth !== this.width || nextHeight !== this.height;
    this.width = nextWidth;
    this.height = nextHeight;
    if (!sizeChanged || !this.currentBackgroundId || this.destroyed) {
      return;
    }

    const cue: BackgroundCue = {
      id: this.currentBackgroundId,
      transition: 'cut',
      duration: 0,
      effect: this.currentEffect,
      effectIntensity: this.currentEffectIntensity
    };
    void this.setBackground(cue);
  }

  public update(deltaMs: number): void {
    const safeDeltaMs = Math.max(0, deltaMs);
    this.effectTimeMs += safeDeltaMs;
    this.updateTransition(safeDeltaMs);
    this.updateEffectAnimation();
    this.updateCameraAnimation();
    this.updateShake(safeDeltaMs);
  }

  public shake(intensity: number, durationMs: number): void {
    this.shakeIntensity = Math.max(0, Math.min(intensity, 1));
    this.shakeTimerMs = Math.max(0, durationMs);
  }

  private clearShake(): void {
    this.shakeTimerMs = 0;
    this.shakeIntensity = 0;
    this.container.position.set(0, 0);
  }

  public getDebugState(): BackgroundDebugState {
    const definition = this.currentBackgroundId
      ? this.getDefinition(this.currentBackgroundId)
      : undefined;
    return {
      currentBackgroundId: this.currentBackgroundId,
      label: definition?.label ?? null,
      category: definition?.category ?? null,
      known: this.currentBackgroundId === null ? false : definition !== undefined,
      currentEffect: this.currentEffect,
      currentEffectIntensity: this.currentEffectIntensity,
      transitioning: this.transitionState !== null,
      placeholder: this.currentLayer?.placeholder ?? true,
      backgroundLayerChildCount: this.backgroundLayer.children.length,
      currentLayerChildCount: this.currentLayer?.container.children.length ?? 0,
      transitionPreviousChildCount: this.transitionState?.previous?.children.length ?? 0,
      effectLayerChildCount: this.effectLayer.children.length,
      effectLayerAlpha: this.effectLayer.alpha,
      effectLayerVisible: this.effectLayer.visible,
      effectLayerX: this.effectLayer.x,
      effectLayerY: this.effectLayer.y,
      backgroundLayerScaleX: this.backgroundLayer.scale.x,
      backgroundLayerScaleY: this.backgroundLayer.scale.y,
      backgroundLayerX: this.backgroundLayer.x,
      backgroundLayerY: this.backgroundLayer.y,
      containerX: this.container.x,
      containerY: this.container.y,
      rainLayerCount: [
        this.rainMistLayer,
        this.rainBackLayer,
        this.rainMidLayer,
        this.rainFrontLayer,
        this.rainSplashLayer
      ].filter(Boolean).length,
      noiseFrameCount: this.noiseFrames.length,
      glitchLayerCount: this.glitchLayers ? 3 + this.glitchLayers.noiseFrames.length : 0,
      shakeActive: this.shakeTimerMs > 0,
      shakeTimerMs: Math.ceil(this.shakeTimerMs),
      effectTimeMs: Math.ceil(this.effectTimeMs)
    };
  }

  public destroy(): void {
    if (this.destroyed) {
      return;
    }

    this.destroyed = true;
    this.requestToken += 1;
    this.transitionState = null;
    this.currentLayer = null;
    this.container.parent?.removeChild(this.container);
    this.container.destroy(DISPLAY_DESTROY_OPTIONS);
  }

  private resolveCue(cue: BackgroundCue | undefined): Required<BackgroundCue> {
    const id = cue?.id ?? DEFAULT_BACKGROUND_ID;
    const definition = this.getDefinition(id);
    return {
      id,
      transition: cue?.transition ?? 'cut',
      duration: cue?.duration ?? 0,
      effect: cue?.effect ?? definition?.defaultEffect ?? 'none',
      effectIntensity: normalizeEffectIntensity(cue?.effectIntensity)
    };
  }

  private async createLayer(cue: Required<BackgroundCue>): Promise<BackgroundLayer> {
    const definition = this.getDefinition(cue.id);
    const layer = new Container();
    const assetPath = definition?.assetPath;
    const image = await this.assetLoader.loadImage({
      id: cue.id,
      kind: 'background',
      path: assetPath
    });

    if (!image.placeholder) {
      const sprite = new Sprite(image.texture);
      coverSprite(sprite, this.width, this.height);
      layer.addChild(sprite);
      return {
        container: layer,
        placeholder: false
      };
    }

    layer.addChild(this.createPlaceholder(definition, cue.id));
    return {
      container: layer,
      placeholder: true
    };
  }

  private createPlaceholder(definition: BackgroundDefinition | undefined, id: string): Container {
    const layer = new Container();
    const colors = definition?.placeholder?.colors ?? ['101010', '5c1010'];
    const primary = parseHexColor(colors[0], UI_COLORS.black);
    const secondary = parseHexColor(colors[1], UI_COLORS.redDark);
    const label = definition?.label ?? definition?.placeholder?.label ?? id;
    const placeholderLabel = definition?.placeholder?.label;
    const gradient = new FillGradient({
      type: 'linear',
      start: { x: 0, y: 0 },
      end: { x: 0, y: this.height },
      textureSpace: 'global',
      colorStops: [
        { offset: 0, color: toHexColor(mixColor(primary, UI_COLORS.black, 0.18)) },
        { offset: 0.52, color: toHexColor(mixColor(primary, secondary, 0.42)) },
        { offset: 1, color: toHexColor(mixColor(secondary, UI_COLORS.black, 0.55)) }
      ]
    });

    const base = new Graphics();
    base.rect(0, 0, this.width, this.height).fill(gradient);
    base.rect(0, 0, this.width, this.height).fill({ color: UI_COLORS.black, alpha: 0.18 });
    base.rect(0, 0, this.width, this.height).stroke({ color: secondary, width: 3, alpha: 0.42 });
    base.rect(this.width * 0.08, this.height * 0.1, this.width * 0.84, 2).fill({
      color: secondary,
      alpha: 0.35
    });
    base.rect(0, this.height * 0.58, this.width, this.height * 0.42).fill({
      color: UI_COLORS.black,
      alpha: 0.36
    });
    layer.addChild(base);

    this.drawPlaceholderMotif(layer, definition, id, primary, secondary);
    this.drawPlaceholderNoise(layer, secondary, CASE0_PLACEHOLDER_IDS.has(id) ? 190 : 110);

    const title = new Text({
      text: label,
      style: {
        fill: UI_COLORS.ink,
        fontFamily: UI_FONT_FAMILY,
        fontSize: 36,
        fontWeight: '900'
      }
    });
    title.anchor.set(0.5);
    title.alpha = 0.28;
    title.position.set(this.width / 2, this.height * 0.31);
    layer.addChild(title);

    const idText = new Text({
      text: id,
      style: {
        fill: UI_COLORS.mutedInk,
        fontFamily: UI_FONT_FAMILY,
        fontSize: 18,
        fontWeight: '800'
      }
    });
    idText.anchor.set(0.5);
    idText.alpha = 0.34;
    idText.position.set(this.width / 2, this.height * 0.39);
    layer.addChild(idText);

    if (placeholderLabel && placeholderLabel !== label) {
      const placeholderText = new Text({
        text: placeholderLabel,
        style: {
          fill: UI_COLORS.ink,
          fontFamily: UI_FONT_FAMILY,
          fontSize: 15,
          fontWeight: '700'
        }
      });
      placeholderText.anchor.set(0.5);
      placeholderText.alpha = 0.22;
      placeholderText.position.set(this.width / 2, this.height * 0.44);
      layer.addChild(placeholderText);
    }

    return layer;
  }

  private drawPlaceholderMotif(
    layer: Container,
    definition: BackgroundDefinition | undefined,
    id: string,
    primary: number,
    secondary: number
  ): void {
    const motif = this.getPlaceholderMotif(definition, id);
    const graphics = new Graphics();
    graphics.alpha = CASE0_PLACEHOLDER_IDS.has(id) ? 0.94 : 0.68;

    if (motif === 'town') {
      this.drawTownMotif(graphics, primary, secondary);
    } else if (motif === 'detectiveOffice') {
      this.drawDetectiveOfficeMotif(graphics, primary, secondary);
    } else if (motif === 'policeStation') {
      this.drawPoliceStationMotif(graphics, secondary);
    } else if (motif === 'jewelryExterior') {
      this.drawJewelryExteriorMotif(graphics, secondary);
    } else if (motif === 'jewelryInterior') {
      this.drawJewelryInteriorMotif(graphics, secondary);
    } else if (motif === 'lockedRoom') {
      this.drawLockedRoomMotif(graphics, secondary);
    } else if (motif === 'detectiveBoard') {
      this.drawDetectiveBoardMotif(graphics, secondary);
    } else if (motif === 'clockTower') {
      this.drawClockTowerMotif(graphics, primary, secondary);
    } else {
      this.drawCaseFileMotif(graphics, secondary);
    }

    layer.addChild(graphics);
  }

  private getPlaceholderMotif(
    definition: BackgroundDefinition | undefined,
    id: string
  ): PlaceholderMotif {
    if (id === 'town_night' || definition?.category === 'town') {
      return 'town';
    }
    if (id === 'detective_office_night') {
      return 'detectiveOffice';
    }
    if (id === 'police_station') {
      return 'policeStation';
    }
    if (id === 'jewelry_shop_exterior') {
      return 'jewelryExterior';
    }
    if (id === 'jewelry_shop_interior') {
      return 'jewelryInterior';
    }
    if (id === 'locked_room') {
      return 'lockedRoom';
    }
    if (id === 'detective_board') {
      return 'detectiveBoard';
    }
    if (id === 'clock_tower_outside') {
      return 'clockTower';
    }
    return 'caseFile';
  }

  private drawTownMotif(graphics: Graphics, primary: number, secondary: number): void {
    const horizon = this.height * 0.58;
    const buildingColor = mixColor(primary, UI_COLORS.black, 0.56);
    for (let index = 0; index < 12; index += 1) {
      const width = 62 + (index % 4) * 24;
      const height = 130 + ((index * 37) % 120);
      const x = index * 108 - 44;
      const y = horizon - height;
      graphics.rect(x, y, width, height).fill({ color: buildingColor, alpha: 0.74 });
      graphics.rect(x, y, width, height).stroke({ color: secondary, width: 1, alpha: 0.16 });
      for (let row = 0; row < 4; row += 1) {
        for (let col = 0; col < 2; col += 1) {
          graphics.rect(x + 16 + col * 24, y + 24 + row * 32, 8, 10).fill({
            color: UI_COLORS.gold,
            alpha: index % 3 === row % 3 ? 0.32 : 0.09
          });
        }
      }
    }
    graphics.rect(0, horizon + 70, this.width, 118).fill({ color: UI_COLORS.black, alpha: 0.32 });
    for (let index = 0; index < 8; index += 1) {
      graphics.rect(170 + index * 145, horizon + 126, 58, 4).fill({
        color: UI_COLORS.gold,
        alpha: 0.22
      });
    }
  }

  private drawDetectiveOfficeMotif(graphics: Graphics, primary: number, secondary: number): void {
    const boardX = this.width * 0.16;
    const boardY = this.height * 0.17;
    const boardWidth = this.width * 0.3;
    const boardHeight = this.height * 0.32;
    graphics.roundRect(boardX, boardY, boardWidth, boardHeight, 10).fill({
      color: mixColor(primary, UI_COLORS.black, 0.34),
      alpha: 0.72
    });
    graphics.roundRect(boardX, boardY, boardWidth, boardHeight, 10).stroke({
      color: secondary,
      width: 3,
      alpha: 0.38
    });
    this.drawPinnedCards(graphics, boardX, boardY, boardWidth, boardHeight, secondary);

    const windowX = this.width * 0.58;
    graphics.rect(windowX, this.height * 0.15, this.width * 0.24, this.height * 0.24).stroke({
      color: UI_COLORS.gold,
      width: 2,
      alpha: 0.28
    });
    graphics.rect(windowX + this.width * 0.12, this.height * 0.15, 2, this.height * 0.24).fill({
      color: UI_COLORS.gold,
      alpha: 0.18
    });
    graphics.rect(this.width * 0.08, this.height * 0.66, this.width * 0.84, 76).fill({
      color: UI_COLORS.black,
      alpha: 0.42
    });
    graphics.rect(this.width * 0.14, this.height * 0.6, this.width * 0.34, 26).fill({
      color: UI_COLORS.paperDark,
      alpha: 0.86
    });
  }

  private drawPoliceStationMotif(graphics: Graphics, secondary: number): void {
    const buildingX = this.width * 0.2;
    const buildingY = this.height * 0.2;
    const buildingWidth = this.width * 0.6;
    const buildingHeight = this.height * 0.44;
    graphics.rect(buildingX, buildingY, buildingWidth, buildingHeight).fill({
      color: UI_COLORS.paperDark,
      alpha: 0.68
    });
    graphics.rect(buildingX, buildingY, buildingWidth, buildingHeight).stroke({
      color: secondary,
      width: 3,
      alpha: 0.42
    });
    graphics.rect(buildingX + 34, buildingY - 46, buildingWidth - 68, 42).fill({
      color: UI_COLORS.black,
      alpha: 0.62
    });
    graphics.rect(buildingX + 34, buildingY - 46, buildingWidth - 68, 42).stroke({
      color: UI_COLORS.gold,
      width: 2,
      alpha: 0.32
    });
    for (let index = 0; index < 6; index += 1) {
      const x = buildingX + 58 + index * ((buildingWidth - 116) / 5);
      graphics.rect(x, buildingY + 76, 34, buildingHeight - 90).fill({
        color: UI_COLORS.black,
        alpha: 0.38
      });
      graphics.rect(x, buildingY + 28, 34, 26).fill({
        color: UI_COLORS.gold,
        alpha: 0.14 + (index % 2) * 0.08
      });
    }
  }

  private drawJewelryExteriorMotif(graphics: Graphics, secondary: number): void {
    const shopX = this.width * 0.16;
    const shopY = this.height * 0.24;
    const shopWidth = this.width * 0.68;
    const shopHeight = this.height * 0.38;
    graphics.rect(shopX, shopY, shopWidth, shopHeight).fill({ color: UI_COLORS.black, alpha: 0.5 });
    graphics.rect(shopX, shopY, shopWidth, shopHeight).stroke({
      color: secondary,
      width: 3,
      alpha: 0.48
    });
    graphics.rect(shopX + 48, shopY + 92, shopWidth - 96, 108).fill({
      color: 0x151b22,
      alpha: 0.78
    });
    graphics.rect(shopX + 48, shopY + 92, shopWidth - 96, 108).stroke({
      color: UI_COLORS.gold,
      width: 2,
      alpha: 0.32
    });
    for (let index = 0; index < 7; index += 1) {
      graphics.rect(shopX + index * 122, shopY + 44, 72, 12).fill({
        color: index % 2 === 0 ? UI_COLORS.red : UI_COLORS.gold,
        alpha: 0.28
      });
    }
    for (let index = 0; index < 6; index += 1) {
      const x = shopX + 80 + index * 118;
      graphics
        .moveTo(x, shopY + shopHeight + 32)
        .lineTo(x + 84, shopY + shopHeight - 18)
        .stroke({
          color: UI_COLORS.gold,
          width: 4,
          alpha: 0.18
        });
    }
  }

  private drawJewelryInteriorMotif(graphics: Graphics, secondary: number): void {
    for (let index = 0; index < 3; index += 1) {
      const x = this.width * (0.18 + index * 0.25);
      const y = this.height * 0.48 + (index % 2) * 28;
      graphics.roundRect(x, y, this.width * 0.18, this.height * 0.12, 8).fill({
        color: 0x111820,
        alpha: 0.82
      });
      graphics.roundRect(x, y, this.width * 0.18, this.height * 0.12, 8).stroke({
        color: UI_COLORS.gold,
        width: 2,
        alpha: 0.34
      });
      for (let gem = 0; gem < 4; gem += 1) {
        const gx = x + 34 + gem * 42;
        const gy = y + 32 + (gem % 2) * 10;
        graphics.circle(gx, gy, 7).fill({
          color: gem % 2 === 0 ? UI_COLORS.red : UI_COLORS.gold,
          alpha: 0.35
        });
      }
    }
    graphics.rect(this.width * 0.16, this.height * 0.22, this.width * 0.68, 44).stroke({
      color: secondary,
      width: 2,
      alpha: 0.32
    });
  }

  private drawLockedRoomMotif(graphics: Graphics, secondary: number): void {
    const doorX = this.width * 0.4;
    const doorY = this.height * 0.15;
    const doorWidth = this.width * 0.2;
    const doorHeight = this.height * 0.5;
    graphics.rect(doorX, doorY, doorWidth, doorHeight).fill({
      color: UI_COLORS.paperDark,
      alpha: 0.82
    });
    graphics.rect(doorX, doorY, doorWidth, doorHeight).stroke({
      color: secondary,
      width: 3,
      alpha: 0.5
    });
    graphics.circle(doorX + doorWidth * 0.78, doorY + doorHeight * 0.52, 10).fill({
      color: UI_COLORS.gold,
      alpha: 0.45
    });
    for (let index = 0; index < 7; index += 1) {
      graphics
        .moveTo(180 + index * 150, this.height * 0.66)
        .lineTo(260 + index * 150, this.height * 0.6)
        .stroke({
          color: UI_COLORS.gold,
          width: 4,
          alpha: 0.18
        });
    }
  }

  private drawDetectiveBoardMotif(graphics: Graphics, secondary: number): void {
    const boardX = this.width * 0.14;
    const boardY = this.height * 0.12;
    const boardWidth = this.width * 0.72;
    const boardHeight = this.height * 0.5;
    graphics.roundRect(boardX, boardY, boardWidth, boardHeight, 12).fill({
      color: UI_COLORS.paperDark,
      alpha: 0.68
    });
    graphics.roundRect(boardX, boardY, boardWidth, boardHeight, 12).stroke({
      color: secondary,
      width: 4,
      alpha: 0.42
    });
    this.drawPinnedCards(graphics, boardX, boardY, boardWidth, boardHeight, secondary);
    const nodes = [
      [boardX + 120, boardY + 94],
      [boardX + 370, boardY + 64],
      [boardX + 580, boardY + 160],
      [boardX + 280, boardY + 260],
      [boardX + 720, boardY + 278]
    ] as const;
    for (let index = 0; index < nodes.length - 1; index += 1) {
      graphics
        .moveTo(nodes[index][0], nodes[index][1])
        .lineTo(nodes[index + 1][0], nodes[index + 1][1])
        .stroke({
          color: UI_COLORS.red,
          width: 2,
          alpha: 0.32
        });
    }
    for (const [x, y] of nodes) {
      graphics.circle(x, y, 8).fill({ color: UI_COLORS.red, alpha: 0.58 });
    }
  }

  private drawClockTowerMotif(graphics: Graphics, primary: number, secondary: number): void {
    const towerX = this.width * 0.43;
    const towerY = this.height * 0.13;
    const towerWidth = this.width * 0.14;
    const towerHeight = this.height * 0.56;
    graphics.rect(towerX, towerY, towerWidth, towerHeight).fill({
      color: mixColor(primary, UI_COLORS.black, 0.45),
      alpha: 0.86
    });
    graphics.rect(towerX, towerY, towerWidth, towerHeight).stroke({
      color: secondary,
      width: 3,
      alpha: 0.48
    });
    graphics.circle(towerX + towerWidth / 2, towerY + 96, 58).fill({
      color: UI_COLORS.paperDark,
      alpha: 0.9
    });
    graphics.circle(towerX + towerWidth / 2, towerY + 96, 58).stroke({
      color: UI_COLORS.gold,
      width: 3,
      alpha: 0.46
    });
    graphics
      .moveTo(towerX + towerWidth / 2, towerY + 96)
      .lineTo(towerX + towerWidth / 2 - 34, towerY + 74)
      .stroke({
        color: UI_COLORS.gold,
        width: 4,
        alpha: 0.48
      });
    graphics
      .moveTo(towerX + towerWidth / 2, towerY + 96)
      .lineTo(towerX + towerWidth / 2 + 30, towerY + 128)
      .stroke({
        color: UI_COLORS.red,
        width: 4,
        alpha: 0.48
      });
    for (let index = 0; index < 5; index += 1) {
      graphics.rect(towerX + 28, towerY + 190 + index * 52, towerWidth - 56, 18).fill({
        color: UI_COLORS.black,
        alpha: 0.36
      });
    }
  }

  private drawCaseFileMotif(graphics: Graphics, secondary: number): void {
    graphics
      .roundRect(this.width * 0.2, this.height * 0.16, this.width * 0.6, this.height * 0.46, 10)
      .fill({
        color: UI_COLORS.paperDark,
        alpha: 0.48
      });
    graphics
      .roundRect(this.width * 0.2, this.height * 0.16, this.width * 0.6, this.height * 0.46, 10)
      .stroke({
        color: secondary,
        width: 3,
        alpha: 0.36
      });
    for (let index = 0; index < 6; index += 1) {
      graphics.rect(this.width * 0.28, this.height * 0.25 + index * 44, this.width * 0.44, 4).fill({
        color: UI_COLORS.gold,
        alpha: 0.16
      });
    }
    graphics.rect(this.width * 0.32, this.height * 0.36, this.width * 0.36, 68).stroke({
      color: UI_COLORS.red,
      width: 5,
      alpha: 0.28
    });
  }

  private drawPinnedCards(
    graphics: Graphics,
    x: number,
    y: number,
    width: number,
    height: number,
    accent: number
  ): void {
    for (let index = 0; index < 5; index += 1) {
      const cardX = x + 36 + (index % 3) * (width * 0.27);
      const cardY = y + 38 + Math.floor(index / 3) * (height * 0.38);
      graphics.rect(cardX, cardY, width * 0.17, height * 0.2).fill({
        color: UI_COLORS.ink,
        alpha: 0.14
      });
      graphics.rect(cardX, cardY, width * 0.17, height * 0.2).stroke({
        color: accent,
        width: 1,
        alpha: 0.22
      });
      graphics.circle(cardX + 12, cardY + 10, 5).fill({ color: UI_COLORS.red, alpha: 0.54 });
    }
  }

  private drawPlaceholderNoise(layer: Container, secondary: number, count: number): void {
    const noise = new Graphics();
    const lightNoise = mixColor(secondary, UI_COLORS.ink, 0.48);
    for (let index = 0; index < count; index += 1) {
      const x = (index * 97 + 31) % this.width;
      const y = (index * 53 + 19) % this.height;
      const width = 1 + (index % 5);
      const height = 1 + (index % 3);
      noise.rect(x, y, width, height).fill({
        color: index % 4 === 0 ? UI_COLORS.black : lightNoise,
        alpha: index % 4 === 0 ? 0.12 : 0.08
      });
    }
    for (let index = 0; index < 18; index += 1) {
      const y = ((index * 41 + 17) % this.height) | 0;
      noise.rect(0, y, this.width, 1).fill({
        color: UI_COLORS.ink,
        alpha: 0.03
      });
    }
    layer.addChild(noise);
  }

  private applyLayerTransition(
    nextLayer: BackgroundLayer,
    transition: BackgroundTransition,
    durationMs: number
  ): void {
    if (this.transitionState) {
      if (this.currentLayer) {
        this.currentLayer.container.alpha = 1;
      }
      this.transitionState.previous?.destroy(DISPLAY_DESTROY_OPTIONS);
      this.transitionState = null;
      this.clearTransitionOverlay();
    }

    const previous = this.currentLayer?.container ?? null;
    if (previous && previous.parent === this.backgroundLayer) {
      this.backgroundLayer.removeChild(previous);
    }

    this.currentLayer = nextLayer;
    this.backgroundLayer.addChild(nextLayer.container);
    const duration = Math.max(0, durationMs);

    if (previous && transition !== 'cut' && transition !== 'none' && duration > 0) {
      previous.alpha = 1;
      this.backgroundLayer.addChildAt(previous, 0);
      nextLayer.container.alpha = 0;
      this.transitionState = {
        elapsedMs: 0,
        durationMs: duration,
        transition,
        previous
      };
      return;
    }

    previous?.destroy(DISPLAY_DESTROY_OPTIONS);
    nextLayer.container.alpha = 1;
    this.transitionState = null;
    this.clearTransitionOverlay();
  }

  private updateTransition(deltaMs: number): void {
    if (!this.transitionState || !this.currentLayer) {
      return;
    }

    this.transitionState.elapsedMs += deltaMs;
    const progress = Math.min(1, this.transitionState.elapsedMs / this.transitionState.durationMs);
    const eased = easeOutCubic(progress);

    if (this.transitionState.transition === 'blackFade') {
      const secondHalfProgress = Math.max(0, (progress - 0.5) * 2);
      const secondHalfEased = easeOutCubic(secondHalfProgress);
      this.currentLayer.container.alpha = secondHalfEased;
      if (this.transitionState.previous) {
        this.transitionState.previous.alpha = 1 - secondHalfEased;
      }
      this.drawTransitionOverlay(UI_COLORS.black, 1 - Math.abs(progress - 0.5) * 2);
    } else {
      this.currentLayer.container.alpha = eased;
      if (this.transitionState.previous) {
        this.transitionState.previous.alpha = 1 - eased;
      }
      if (this.transitionState.transition === 'whiteFlash') {
        this.drawTransitionOverlay(0xffffff, Math.sin(progress * Math.PI) * 0.82);
      } else {
        this.clearTransitionOverlay();
      }
    }

    if (progress >= 1) {
      this.transitionState.previous?.destroy(DISPLAY_DESTROY_OPTIONS);
      this.transitionState = null;
      this.currentLayer.container.alpha = 1;
      this.clearTransitionOverlay();
    }
  }

  private drawEffectLayer(): void {
    this.effectLayer.removeChildren().forEach((child) => child.destroy(DISPLAY_DESTROY_OPTIONS));
    this.effectLayer.position.set(0, 0);
    this.effectLayer.alpha = 1;
    this.effectLayer.visible = true;
    this.rainMistLayer = null;
    this.rainBackLayer = null;
    this.rainMidLayer = null;
    this.rainFrontLayer = null;
    this.rainSplashLayer = null;
    this.noiseFrames = [];
    this.glitchLayers = null;
    const layer = new Graphics();
    let hasEffect = false;
    if (this.currentEffect === 'rain') {
      this.drawRainEffect();
      return;
    } else if (this.currentEffect === 'noise') {
      this.drawNoiseEffect(false);
      return;
    } else if (this.currentEffect === 'staticNoise') {
      this.drawNoiseEffect(true);
      return;
    } else if (this.currentEffect === 'glitch') {
      this.drawGlitchEffect();
      return;
    } else if (this.currentEffect === 'darken') {
      hasEffect = true;
      layer.rect(0, 0, this.width, this.height).fill({ color: UI_COLORS.black, alpha: 0.28 });
    }

    if (hasEffect) {
      this.effectLayer.addChild(layer);
    }
  }

  private drawRainEffect(): void {
    const intensity = this.currentEffectIntensity;
    const mistLayer = new Graphics();
    const backLayer = new Graphics();
    const midLayer = new Graphics();
    const frontLayer = new Graphics();
    const splashLayer = new Graphics();

    drawRainMist(mistLayer, this.width, this.height, intensity);
    drawRainStreaks(backLayer, this.width, this.height, {
      count: Math.round(132 + intensity * 86),
      length: 34 + intensity * 18,
      slant: 15 + intensity * 8,
      strokeWidth: 1,
      alpha: 0.085 + intensity * 0.055,
      color: 0x9fc7df,
      seedX: 47,
      seedY: 83,
      cycleY: RAIN_BACK_CYCLE_Y,
      highlightEvery: 17,
      motionBlur: false
    });
    drawRainStreaks(midLayer, this.width, this.height, {
      count: Math.round(88 + intensity * 66),
      length: 62 + intensity * 24,
      slant: 24 + intensity * 12,
      strokeWidth: 1.2,
      alpha: 0.13 + intensity * 0.075,
      color: 0xbfdcf2,
      seedX: 61,
      seedY: 127,
      cycleY: RAIN_MID_CYCLE_Y,
      highlightEvery: 8,
      motionBlur: true
    });
    drawRainStreaks(frontLayer, this.width, this.height, {
      count: Math.round(42 + intensity * 52),
      length: 98 + intensity * 42,
      slant: 38 + intensity * 16,
      strokeWidth: 1.9,
      alpha: 0.21 + intensity * 0.12,
      color: 0xe7f5ff,
      seedX: 73,
      seedY: 59,
      cycleY: RAIN_FRONT_CYCLE_Y,
      highlightEvery: 4,
      motionBlur: true
    });
    drawRainSplashes(splashLayer, this.width, this.height, intensity);

    this.rainMistLayer = mistLayer;
    this.rainBackLayer = backLayer;
    this.rainMidLayer = midLayer;
    this.rainFrontLayer = frontLayer;
    this.rainSplashLayer = splashLayer;
    this.effectLayer.addChild(mistLayer);
    this.effectLayer.addChild(backLayer);
    this.effectLayer.addChild(midLayer);
    this.effectLayer.addChild(frontLayer);
    this.effectLayer.addChild(splashLayer);
  }

  private drawNoiseEffect(staticMode: boolean): void {
    const intensity = this.currentEffectIntensity;
    const vignette = new Graphics();
    vignette.rect(0, 0, this.width, this.height).fill({
      color: UI_COLORS.black,
      alpha: staticMode ? 0.055 + intensity * 0.035 : 0.025 + intensity * 0.025
    });
    vignette.rect(0, this.height * 0.72, this.width, this.height * 0.28).fill({
      color: UI_COLORS.black,
      alpha: staticMode ? 0.06 : 0.035
    });
    this.effectLayer.addChild(vignette);

    const frameCount = staticMode ? STATIC_NOISE_FRAME_COUNT : NOISE_FRAME_COUNT;
    for (let frameIndex = 0; frameIndex < frameCount; frameIndex += 1) {
      const frame = new Graphics();
      drawNoiseFrame(frame, this.width, this.height, {
        count: Math.round((staticMode ? 118 : 76) + intensity * (staticMode ? 82 : 54)),
        minSize: staticMode ? 1 : 1,
        maxSize: staticMode ? 4 : 3,
        alpha: staticMode ? 0.08 + intensity * 0.055 : 0.045 + intensity * 0.035,
        lightColor: UI_COLORS.ink,
        darkColor: UI_COLORS.black,
        seedOffset: frameIndex * 37 + (staticMode ? 211 : 79),
        scanlineAlpha: staticMode ? 0.055 + intensity * 0.035 : 0.025 + intensity * 0.02,
        bandAlpha: staticMode ? 0.045 + intensity * 0.035 : 0.014 + intensity * 0.012
      });
      frame.visible = frameIndex === 0;
      this.noiseFrames.push(frame);
      this.effectLayer.addChild(frame);
    }
  }

  private drawGlitchEffect(): void {
    const intensity = this.currentEffectIntensity;
    const scanlines = new Graphics();
    const colorShift = new Graphics();
    const slices = new Graphics();
    const noiseFrames: Graphics[] = [];

    for (let index = 0; index < 14; index += 1) {
      const y = 24 + index * 50;
      scanlines.rect(0, y, this.width, 1).fill({
        color: UI_COLORS.ink,
        alpha: 0.025 + intensity * 0.018
      });
    }

    for (let index = 0; index < 8; index += 1) {
      const y = 52 + index * 74;
      const height = 3 + (index % 3) * 2;
      colorShift.rect(-24, y, this.width + 48, height).fill({
        color: UI_COLORS.red,
        alpha: 0.09 + intensity * 0.045
      });
      colorShift.rect(18, y + 7, this.width, Math.max(2, height - 1)).fill({
        color: 0x45c7ff,
        alpha: 0.075 + intensity * 0.04
      });
    }

    for (let index = 0; index < 9; index += 1) {
      const y = 42 + ((index * 83 + 17) % Math.max(1, this.height - 84));
      const x = 28 + ((index * 139 + 43) % Math.max(1, this.width - 260));
      const width = 180 + (index % 4) * 76;
      const height = 10 + (index % 3) * 8;
      slices.rect(x, y, width, height).fill({
        color: index % 2 === 0 ? UI_COLORS.red : UI_COLORS.gold,
        alpha: 0.06 + intensity * 0.05
      });
      slices.rect(Math.max(0, x - 42), y + height + 4, width * 0.72, 3).fill({
        color: 0x45c7ff,
        alpha: 0.055 + intensity * 0.035
      });
    }

    for (let frameIndex = 0; frameIndex < GLITCH_NOISE_FRAME_COUNT; frameIndex += 1) {
      const frame = new Graphics();
      drawNoiseFrame(frame, this.width, this.height, {
        count: Math.round(72 + intensity * 64),
        minSize: 1,
        maxSize: 4,
        alpha: 0.055 + intensity * 0.045,
        lightColor: UI_COLORS.ink,
        darkColor: UI_COLORS.black,
        seedOffset: 311 + frameIndex * 61,
        scanlineAlpha: 0.035 + intensity * 0.035,
        bandAlpha: 0.035 + intensity * 0.035
      });
      frame.visible = false;
      noiseFrames.push(frame);
    }

    colorShift.visible = false;
    slices.visible = false;
    this.glitchLayers = {
      scanlines,
      colorShift,
      slices,
      noiseFrames
    };
    this.effectLayer.addChild(scanlines);
    this.effectLayer.addChild(colorShift);
    this.effectLayer.addChild(slices);
    for (const frame of noiseFrames) {
      this.effectLayer.addChild(frame);
    }
  }

  private updateEffectAnimation(): void {
    if (this.currentEffect === 'rain') {
      this.updateRainAnimation();
    } else if (this.currentEffect === 'noise') {
      this.updateNoiseAnimation(false);
    } else if (this.currentEffect === 'staticNoise') {
      this.updateNoiseAnimation(true);
    } else if (this.currentEffect === 'glitch') {
      this.updateGlitchAnimation();
    } else {
      this.effectLayer.position.set(0, 0);
      this.effectLayer.alpha = 1;
      this.effectLayer.visible = true;
    }
  }

  private updateRainAnimation(): void {
    const intensity = this.currentEffectIntensity;
    const backCycleX = 28;
    const midCycleX = 36;
    const frontCycleX = 42;
    this.effectLayer.position.set(0, 0);
    this.effectLayer.alpha = 0.9 + intensity * 0.08;
    if (this.rainMistLayer) {
      this.rainMistLayer.position.set(
        0,
        (this.effectTimeMs / (150 - intensity * 32)) % RAIN_MIST_CYCLE_Y
      );
      this.rainMistLayer.alpha = 0.78 + Math.sin(this.effectTimeMs / 760) * 0.05;
    }
    if (this.rainBackLayer) {
      this.rainBackLayer.position.set(
        -((this.effectTimeMs / (110 - intensity * 28)) % backCycleX),
        (this.effectTimeMs / (54 - intensity * 10)) % RAIN_BACK_CYCLE_Y
      );
    }
    if (this.rainMidLayer) {
      this.rainMidLayer.position.set(
        -((this.effectTimeMs / (84 - intensity * 22)) % midCycleX),
        (this.effectTimeMs / (42 - intensity * 8)) % RAIN_MID_CYCLE_Y
      );
    }
    if (this.rainFrontLayer) {
      this.rainFrontLayer.position.set(
        -((this.effectTimeMs / (68 - intensity * 16)) % frontCycleX),
        (this.effectTimeMs / (32 - intensity * 5)) % RAIN_FRONT_CYCLE_Y
      );
    }
    if (this.rainSplashLayer) {
      this.rainSplashLayer.alpha = 0.62 + Math.sin(this.effectTimeMs / 88) * 0.12;
    }
  }

  private updateNoiseAnimation(staticMode: boolean): void {
    if (this.noiseFrames.length <= 0) {
      return;
    }

    const intensity = this.currentEffectIntensity;
    const frameMs = staticMode ? 70 : 130;
    const activeFrame = Math.floor(this.effectTimeMs / frameMs) % this.noiseFrames.length;
    for (let index = 0; index < this.noiseFrames.length; index += 1) {
      this.noiseFrames[index].visible = index === activeFrame;
    }
    this.effectLayer.alpha =
      (staticMode ? 0.56 + intensity * 0.18 : 0.34 + intensity * 0.16) +
      (Math.floor(this.effectTimeMs / (staticMode ? 92 : 180)) % 2) * (staticMode ? 0.05 : 0.025);
    this.effectLayer.position.set(staticMode && activeFrame % 2 === 1 ? 1 : 0, 0);
  }

  private updateGlitchAnimation(): void {
    const layers = this.glitchLayers;
    if (!layers) {
      return;
    }

    const intensity = this.currentEffectIntensity;
    const phase = this.effectTimeMs % GLITCH_LOOP_MS;
    const burst =
      phase <= GLITCH_BURST_DURATION_MS ||
      (phase >= GLITCH_SECONDARY_BURST_START_MS && phase <= GLITCH_SECONDARY_BURST_END_MS);
    const tick = Math.floor(this.effectTimeMs / 58);
    const direction = tick % 2 === 0 ? 1 : -1;
    const offset = burst ? direction * (4 + intensity * 8) : 0;
    const activeFrame = layers.noiseFrames.length
      ? Math.floor(this.effectTimeMs / 68) % layers.noiseFrames.length
      : -1;

    this.effectLayer.alpha = burst ? 0.48 + intensity * 0.22 : 0.18 + intensity * 0.08;
    this.effectLayer.position.set(offset * 0.34, 0);
    layers.scanlines.position.set(burst ? -offset * 0.22 : 0, 0);
    layers.colorShift.visible = burst;
    layers.colorShift.position.set(offset, 0);
    layers.slices.visible = burst;
    layers.slices.position.set(-offset * 0.8, 0);
    for (let index = 0; index < layers.noiseFrames.length; index += 1) {
      layers.noiseFrames[index].visible = burst && index === activeFrame;
    }
  }

  private updateCameraAnimation(): void {
    const camera = getCameraTransform(
      this.currentEffect,
      this.effectTimeMs,
      this.width,
      this.height
    );
    this.backgroundLayer.scale.set(camera.scale);
    this.backgroundLayer.position.set(camera.x, camera.y);
  }

  private updateShake(deltaMs: number): void {
    if (this.shakeTimerMs <= 0) {
      this.container.position.set(0, 0);
      return;
    }

    this.shakeTimerMs = Math.max(0, this.shakeTimerMs - deltaMs);
    const phase = Math.ceil(this.shakeTimerMs / 34);
    const amount = this.shakeIntensity * 12;
    this.container.position.set(phase % 2 === 0 ? amount : -amount, phase % 3 === 0 ? -4 : 4);

    if (this.shakeTimerMs <= 0) {
      this.container.position.set(0, 0);
    }
  }

  private getDefinition(id: string): BackgroundDefinition | undefined {
    return this.backgroundById.get(id);
  }

  private drawTransitionOverlay(color: number, alpha: number): void {
    this.transitionOverlay.clear();
    const clampedAlpha = Math.max(0, Math.min(alpha, 1));
    if (clampedAlpha <= 0) {
      return;
    }

    this.transitionOverlay.rect(0, 0, this.width, this.height).fill({
      color,
      alpha: clampedAlpha
    });
  }

  private clearTransitionOverlay(): void {
    this.transitionOverlay.clear();
  }
}

function coverSprite(sprite: Sprite, width: number, height: number): void {
  const textureWidth = Math.max(1, sprite.texture.width);
  const textureHeight = Math.max(1, sprite.texture.height);
  const scale = Math.max(width / textureWidth, height / textureHeight);
  sprite.scale.set(scale);
  sprite.position.set((width - textureWidth * scale) / 2, (height - textureHeight * scale) / 2);
}

function parseHexColor(value: string, fallback: number): number {
  const normalized = value.startsWith('#') ? value.slice(1) : value;
  const parsed = Number.parseInt(normalized, 16);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function normalizeEffectIntensity(value: number | undefined): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return DEFAULT_EFFECT_INTENSITY;
  }

  return Math.max(0, Math.min(value, 1));
}

function drawRainStreaks(
  graphics: Graphics,
  width: number,
  height: number,
  config: RainLayerConfig
): void {
  const fieldWidth = width + 320;
  const fieldHeight = height + config.cycleY + 180;
  for (let index = 0; index < config.count; index += 1) {
    const depth = ((index * 37 + config.seedY) % 100) / 100;
    const x = ((index * config.seedX + 31) % fieldWidth) - 150;
    const y = ((index * config.seedY + 17) % fieldHeight) - config.cycleY - 82;
    const length = config.length * (0.7 + depth * 0.72) + (index % 5) * 3;
    const slant = config.slant * (0.78 + depth * 0.5);
    const flicker = 0.66 + (((index * 19 + config.seedX) % 29) / 29) * 0.34;
    const alpha = config.alpha * flicker * (index % config.highlightEvery === 0 ? 1.22 : 1);
    graphics
      .moveTo(x, y)
      .lineTo(x - slant, y + length)
      .stroke({
        color: config.color,
        width: config.strokeWidth,
        alpha
      });

    if (config.motionBlur && index % config.highlightEvery === 0) {
      graphics
        .moveTo(x + 2, y - 6)
        .lineTo(x - slant * 0.58 + 2, y + length * 0.52)
        .stroke({
          color: 0xffffff,
          width: Math.max(1, config.strokeWidth * 0.68),
          alpha: alpha * 0.3
        });
    }
  }
}

function drawRainMist(graphics: Graphics, width: number, height: number, intensity: number): void {
  graphics.rect(0, 0, width, height).fill({
    color: 0x172836,
    alpha: 0.024 + intensity * 0.026
  });
  graphics.rect(0, height * 0.54, width, height * 0.46).fill({
    color: 0x7caec3,
    alpha: 0.038 + intensity * 0.05
  });

  for (let index = 0; index < 7; index += 1) {
    const y = height * (0.34 + index * 0.085);
    graphics.rect(0, y, width, 8 + index * 2).fill({
      color: index % 2 === 0 ? 0xb2d5e8 : 0x4e7182,
      alpha: 0.018 + intensity * 0.01 + index * 0.003
    });
  }

  for (let index = 0; index < 10; index += 1) {
    const y = (index * 83 + 31) % (height + 120);
    graphics
      .moveTo(-80, y)
      .lineTo(width + 120, y - 22 - (index % 3) * 8)
      .stroke({
        color: 0xc4d8e7,
        width: 1,
        alpha: 0.018 + intensity * 0.008
      });
  }
}

function drawRainSplashes(
  graphics: Graphics,
  width: number,
  height: number,
  intensity: number
): void {
  const groundY = height - 26;
  const splashCount = Math.round(42 + intensity * 32);
  for (let index = 0; index < splashCount; index += 1) {
    const x = (index * 97 + 43) % width;
    const y = groundY - ((index * 13) % 44);
    const spread = 4 + (index % 6) * 3;
    const alpha = 0.045 + intensity * 0.035 + (index % 3) * 0.012;

    graphics
      .moveTo(x - spread, y)
      .lineTo(x + spread, y - 2)
      .stroke({ color: 0xd8efff, width: 1, alpha });

    if (index % 2 === 0) {
      graphics
        .moveTo(x, y)
        .lineTo(x - spread * 0.42, y - 6 - (index % 4))
        .stroke({ color: 0xf1fbff, width: 1, alpha: alpha * 0.72 });
    }
    if (index % 5 === 0) {
      graphics.circle(x + spread * 0.4, y - 3, 1.1).fill({
        color: 0xe8f6ff,
        alpha: alpha * 0.58
      });
    }
  }
}

function drawNoiseFrame(
  graphics: Graphics,
  width: number,
  height: number,
  config: NoiseFrameConfig
): void {
  for (let index = 0; index < config.count; index += 1) {
    const x = (index * 97 + config.seedOffset * 13 + 19) % width;
    const y = (index * 53 + config.seedOffset * 29 + 31) % height;
    const sizeRange = Math.max(1, config.maxSize - config.minSize + 1);
    const dotWidth = config.minSize + ((index + config.seedOffset) % sizeRange);
    const dotHeight = 1 + ((index + config.seedOffset * 2) % Math.max(1, sizeRange - 1));
    const light = (index + config.seedOffset) % 5 !== 0;
    graphics.rect(x, y, dotWidth, dotHeight).fill({
      color: light ? config.lightColor : config.darkColor,
      alpha: config.alpha * (light ? 1 : 0.7)
    });
  }

  for (let index = 0; index < 18; index += 1) {
    const y = (index * 41 + config.seedOffset * 7) % height;
    graphics.rect(0, y, width, 1).fill({
      color: UI_COLORS.ink,
      alpha: config.scanlineAlpha
    });
  }

  for (let index = 0; index < 4; index += 1) {
    const y = (index * 137 + config.seedOffset * 11) % height;
    graphics.rect(0, y, width, 10 + ((index + config.seedOffset) % 18)).fill({
      color: index % 2 === 0 ? UI_COLORS.black : UI_COLORS.ink,
      alpha: config.bandAlpha
    });
  }
}

function mixColor(first: number, second: number, amount: number): number {
  const ratio = Math.max(0, Math.min(amount, 1));
  const inverseRatio = 1 - ratio;
  const red = ((first >> 16) & 0xff) * inverseRatio + ((second >> 16) & 0xff) * ratio;
  const green = ((first >> 8) & 0xff) * inverseRatio + ((second >> 8) & 0xff) * ratio;
  const blue = (first & 0xff) * inverseRatio + (second & 0xff) * ratio;
  return (Math.round(red) << 16) + (Math.round(green) << 8) + Math.round(blue);
}

function toHexColor(color: number): string {
  return `#${color.toString(16).padStart(6, '0')}`;
}

function easeOutCubic(value: number): number {
  return 1 - Math.pow(1 - value, 3);
}

function getCameraTransform(
  effect: BackgroundEffect,
  elapsedMs: number,
  width: number,
  height: number
): CameraTransform {
  if (effect === 'zoomIn') {
    const progress = easeInOutSine(Math.min(1, elapsedMs / ZOOM_IN_DURATION_MS));
    return centerCamera(1 + progress * 0.038, width, height);
  }

  if (effect === 'zoomOut') {
    const progress = easeInOutSine(Math.min(1, elapsedMs / ZOOM_OUT_DURATION_MS));
    return centerCamera(1.04 - progress * 0.04, width, height);
  }

  if (effect === 'slowPan') {
    const progress = repeatingWave(elapsedMs, SLOW_PAN_DURATION_MS);
    const verticalProgress = repeatingWave(
      elapsedMs + SLOW_PAN_DURATION_MS * 0.22,
      SLOW_PAN_DURATION_MS
    );
    return panCamera(1.04, width, height, 0.18 + progress * 0.64, 0.26 + verticalProgress * 0.48);
  }

  if (effect === 'subtleDrift') {
    const xWave = Math.sin((elapsedMs / SUBTLE_DRIFT_X_DURATION_MS) * Math.PI * 2);
    const yWave = Math.cos((elapsedMs / SUBTLE_DRIFT_Y_DURATION_MS) * Math.PI * 2);
    const scale = 1.018 + xWave * 0.003;
    const centered = centerCamera(scale, width, height);
    const overscanX = width * (scale - 1);
    const overscanY = height * (scale - 1);
    return {
      scale,
      x: centered.x + overscanX * 0.18 * xWave,
      y: centered.y + overscanY * 0.14 * yWave
    };
  }

  return {
    scale: 1,
    x: 0,
    y: 0
  };
}

function centerCamera(scale: number, width: number, height: number): CameraTransform {
  return {
    scale,
    x: -((width * scale - width) / 2),
    y: -((height * scale - height) / 2)
  };
}

function panCamera(
  scale: number,
  width: number,
  height: number,
  horizontalRatio: number,
  verticalRatio: number
): CameraTransform {
  return {
    scale,
    x: -(width * (scale - 1) * horizontalRatio),
    y: -(height * (scale - 1) * verticalRatio)
  };
}

function repeatingWave(elapsedMs: number, durationMs: number): number {
  const duration = Math.max(1, durationMs);
  const progress = ((elapsedMs % duration) + duration) % duration;
  return (1 - Math.cos((progress / duration) * Math.PI * 2)) / 2;
}

function easeInOutSine(value: number): number {
  const progress = Math.max(0, Math.min(value, 1));
  return -(Math.cos(Math.PI * progress) - 1) / 2;
}
